import type { MetaHTMLAttributes } from 'react'
import { useCallback, useEffect } from 'react'
import { useParams } from 'react-router'
import { useSelector } from 'react-redux'
import Immutable from 'immutable'

import { DocumentMeta } from '../components/DocumentMeta'
import { addLegacyProductProperties, track } from '../utils/tracking'
import { addProduct, setAction } from '../utils/googleAnalytics'
import { createMultiLanguageLinks } from './utils/createMultiLanguageLinks'
import { getSocialSharingEnabled, loadCart, loadProduct } from '../store/actions'
import Theme from '../components/Theme'
import useEffectExceptMount from '../utils/hooks/useEffectExceptMount'
import usePrevious from '../utils/hooks/usePrevious'

export default function Product() {
  const params = useParams()
  const productId = params.productId

  const product = useSelector<State, ImmutableMap>((state) => state.get('products').get(productId))
  const viewError = useSelector<State, ImmutableMap>((state) => state.getIn(['view', 'error']))
  const shop = useSelector<State, ImmutableMap>((state) => state.get('shop'))
  const cart = useSelector<State, ImmutableMap>((state) => state.get('cart'))

  // Tracking
  const trackProductDetails = useCallback(
    (product: Frontend.Product, cart: Frontend.Cart) => {
      track('product:view', {
        product: addLegacyProductProperties(product, shop.get('beyond')),
        cart,
      })
      addProduct(product, ['id', 'name', 'variant', 'brand', 'price', 'currency'])
      setAction('detail')
    },
    [shop],
  )
  const previousProduct = usePrevious(product)
  useEffectExceptMount(() => {
    if (product && !previousProduct?.equals(product)) {
      trackProductDetails(product.toJS(), cart.toJS())
    }
  }, [previousProduct, product, trackProductDetails, cart])
  useEffect(() => {
    if (product) trackProductDetails(product.toJS(), cart.toJS())
    // It should only trigger on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (viewError || !product) {
    return (
      <>
        <DocumentMeta />
        <Theme withLayout error={viewError} currentView="product" />
      </>
    )
  }

  const productTitle = product.get('title') || product.get('name')
  const documentTitle = product.get('title')
    ? productTitle
    : [productTitle, shop.get('title')].filter(Boolean).join(' - ')

  // In multi language shops, the default locale will not get a localized path, but any other language requires it.
  const pathLocale = shop.get('defaultLocale') === shop.get('locale') ? '' : `/${shop.get('locale').substr(0, 2)}`
  const baseHref = shop.getIn(['locationBase', 'origin']) + pathLocale

  const meta: MetaHTMLAttributes<HTMLMetaElement>[] = []
  meta.push(
    { property: 'og:type', content: 'product' },
    { property: 'og:title', content: productTitle },
    { property: 'og:url', content: baseHref + product.get('href') },
  )

  if (product.get('image')) {
    let productImageUrl = product.getIn(['image', 'url'])

    if (!productImageUrl.startsWith('http')) productImageUrl = shop.getIn(['locationBase', 'origin']) + productImageUrl

    meta.push(
      { property: 'og:image', content: productImageUrl },
      { property: 'og:image:width', content: product.getIn(['image', 'width']) },
      { property: 'og:image:height', content: product.getIn(['image', 'height']) },
    )
  }
  if (product.get('price')) {
    meta.push(
      { property: 'og:price:amount', content: product.getIn(['price', 'amount']) },
      { property: 'og:price:currency', content: product.getIn(['price', 'currency']) },
    )
  }
  if (product.get('metaDescription')) {
    meta.push(
      { name: 'description', content: product.get('metaDescription') },
      { property: 'og:description', content: product.get('metaDescription') },
    )
  }

  return (
    <>
      <DocumentMeta
        title={documentTitle}
        meta={meta}
        links={
          product.get('isVariationProduct')
            ? [
                {
                  rel: 'canonical',
                  href: `${baseHref}/p/${product.getIn(['variationMaster', 'slug'])}`,
                },
              ]
            : createMultiLanguageLinks(
                'product',
                product.get('slugs', Immutable.List()).toJS(),
                shop,
                product.get('productId'),
              )
        }
      />
      <Theme withLayout currentView="product">
        {(renderView, props) =>
          renderView('Product', {
            ...props,
            product: product.toJS(),
          })
        }
      </Theme>
    </>
  )
}

Product.storeUpdate = (props: RouteComponentProps<'productId'>, state: State) => {
  const sellingCountryId = state.getIn(['shop', 'sellingCountryId']) || null

  // On the server, the product is already loaded by `maybeRedirectProduct` in "serveApp/redirects".
  const isForwardNavigationOnClient = props.location.action === 'PUSH' && typeof window !== 'undefined'

  const productId = props.params.productId
  // look for the GUID to the requested slug somewhere in the state in order to save a db roundtrip
  let guid

  const categoryProducts = state
    .get('categoryProductData', Immutable.Map())
    .valueSeq()
    .map((a) => a.products)
    .reduce((a, b) => a.concat(b), [])
  const searchProducts = state
    .get('searchData', Immutable.Map())
    .valueSeq()
    .map((a) => a.products)
    .reduce((a, b) => a.concat(b), [])
  const productWithSlug = [...categoryProducts, ...searchProducts]
    .filter(Boolean)
    .find((product) => product.slug === productId)

  if (productWithSlug) guid = productWithSlug.productId

  const updates: any[] = []

  if (!state.hasIn(['shop', 'socialSharingEnabled'])) updates.push(getSocialSharingEnabled())

  if (!state.get('cart')) updates.push(loadCart())
  if (isForwardNavigationOnClient || !state.getIn(['products', productId])) {
    // Note: This call should match with the `loadProduct` call in
    // `maybeRedirectProduct` in "serveApp/redirects".
    updates.push(
      loadProduct(
        {
          guid: guid || productId,
          slug: guid && productId,
        },
        sellingCountryId,
      ),
    )
  }

  return updates
}

Product.loadableSsrChunks = ['Product']
Product.contentCreationDisabled = () => true
